import { useState } from "react";
import { useNavigate } from "react-router";
import { Download, Fingerprint, LogOut, Pencil } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Sheet } from "@/components/Sheet";
import { AddKidPasskeySheet } from "@/components/SwitchUser";
import { Button, Card, cx, EmptyState, Field, PageLoader, SectionTitle, Select, Stat, TextField } from "@/components/ui";
import { Avatar, kidColor, Points } from "@/components/bits";
import { EmojiAvatarPicker } from "@/components/EmojiPicker";
import { api } from "@/lib/api";
import { passkeysSupported } from "@/lib/passkeys";
import { LEDGER_LABELS, timeAgo } from "@/lib/format";
import { useInstall } from "@/lib/pwa";
import { useAction, useKidSummary, useLedger, useRewardTypes } from "@/lib/queries";
import { useLogout, useSession } from "@/lib/session";
import { COLORS } from "@shared/templates";
import { screenLevelFor } from "@shared/screenLevel";
import { isConvertible } from "@shared/economy";
import type { LedgerEntryDTO } from "@shared/types";

function ProfileSheet({ onClose }: { onClose: () => void }) {
  const { user } = useSession();
  const rewards = useRewardTypes();
  const [form, setForm] = useState({
    avatar: user.avatar,
    color: user.color,
    interests: user.interests ?? "",
    favoriteRewardTypeId: user.favoriteRewardTypeId ?? "",
  });
  const save = useAction(
    () =>
      api.patch(`/family/kids/${user.id}`, {
        avatar: form.avatar,
        color: form.color,
        interests: form.interests.trim() || null,
        favoriteRewardTypeId: form.favoriteRewardTypeId || null,
      }),
    { success: "Looking good!", onSuccess: onClose },
  );
  const options = (rewards.data?.rewardTypes ?? []).filter((t) => t.active && isConvertible(t));
  return (
    <Sheet open onClose={onClose} title="Make it yours" footer={<Button block onClick={() => save.mutate()} loading={save.isPending}>Save</Button>}>
      <div className="space-y-5">
        <div className="flex justify-center">
          <Avatar member={{ ...user, avatar: form.avatar, color: form.color }} size="xl" />
        </div>
        <Field label="Avatar">
          <EmojiAvatarPicker value={form.avatar} onChange={(avatar) => setForm({ ...form, avatar })} />
        </Field>
        <Field label="Color">
          <div className="flex flex-wrap gap-2">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={c}
                onClick={() => setForm({ ...form, color: c })}
                className={cx("size-10 rounded-full transition", form.color === c && "ring-4 ring-offset-2 ring-offset-card")}
                style={{ background: kidColor(c).bg, ["--tw-ring-color" as string]: kidColor(c).ring }}
              />
            ))}
          </div>
        </Field>
        {options.length > 0 && (
          <Field label="Show my earnings as…" hint="Pick the reward that matters most to you.">
            <Select value={form.favoriteRewardTypeId} onChange={(e) => setForm({ ...form, favoriteRewardTypeId: e.target.value })}>
              <option value="">Automatic</option>
              {options.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.icon} {t.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <TextField
          label="Things I love"
          placeholder="Minecraft, soccer, drawing"
          value={form.interests}
          maxLength={300}
          onChange={(e) => setForm({ ...form, interests: e.target.value })}
          hint="Helps your parents pick rewards you'll actually want. Just things you like, no personal details."
        />
      </div>
    </Sheet>
  );
}

/** Where the last month's points came from and went, for kids old enough to want the numbers. */
function MoneyPicture({ entries }: { entries: LedgerEntryDTO[] }) {
  const { family } = useSession();
  const since = Date.now() - 30 * 86_400_000;
  const recent = entries.filter((e) => e.createdAt >= since);
  const earned = recent.filter((e) => e.amount > 0).reduce((sum, e) => sum + e.amount, 0);
  const spent = recent.filter((e) => e.amount < 0).reduce((sum, e) => sum - e.amount, 0);
  return (
    <Card className="mt-3">
      <div className="text-sm font-bold text-muted">Last 30 days</div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
        <div>
          <Points value={earned} family={family} className="text-lg text-brand" />
          <div className="text-muted">In</div>
        </div>
        <div>
          <Points value={spent} family={family} className="text-lg text-danger" />
          <div className="text-muted">Out</div>
        </div>
        <div>
          <div className="text-lg font-extrabold">{earned > 0 ? `${Math.max(0, Math.round(((earned - spent) / earned) * 100))}%` : "—"}</div>
          <div className="text-muted">Kept</div>
        </div>
      </div>
    </Card>
  );
}

export function KidMe() {
  const { user, family } = useSession();
  const summary = useKidSummary(user.id);
  const ledger = useLedger(user.id, 100);
  const logout = useLogout();
  const navigate = useNavigate();
  const install = useInstall();
  const [editing, setEditing] = useState(false);
  const [addingPasskey, setAddingPasskey] = useState(false);

  if (!summary.data) return <PageLoader />;
  const s = summary.data;
  return (
    <div>
      <PageHeader title="Me" bell="/kid/notifications" />
      <Card className="mt-3 flex items-center gap-4">
        <Avatar member={user} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="text-xl font-black">{user.name}</div>
          <div className="truncate text-sm text-muted">{user.interests || family.name}</div>
        </div>
        <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
          <Pencil className="size-4" /> Edit
        </Button>
      </Card>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <Stat label="Balance" value={<Points value={s.balance} family={family} />} tone={s.balance < 0 ? "danger" : "coin"} />
        <Stat label="This week" value={<Points value={s.earnedThisWeek} family={family} />} tone="brand" />
        <Stat label="On hold" value={<Points value={s.pendingSpend} family={family} />} />
      </div>

      {screenLevelFor(user.age, user.screenLevel) === "older" && ledger.data && <MoneyPicture entries={ledger.data.entries} />}

      <SectionTitle>History</SectionTitle>
      {!ledger.data ? (
        <PageLoader />
      ) : ledger.data.entries.length === 0 ? (
        <EmptyState icon="🧾" title="Nothing yet">
          Your earnings and spending will show up here.
        </EmptyState>
      ) : (
        <Card className="divide-y divide-line p-0">
          {ledger.data.entries.map((e) => (
            <div key={e.id} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="truncate font-bold">{e.reason || LEDGER_LABELS[e.kind]}</div>
                <div className="truncate text-xs text-muted">
                  {LEDGER_LABELS[e.kind]} · {timeAgo(e.createdAt)}
                  {e.note ? ` · ${e.note}` : ""}
                </div>
              </div>
              <Points value={e.amount} family={family} signed className={e.amount >= 0 ? "text-brand" : "text-danger"} />
            </div>
          ))}
        </Card>
      )}

      {!install.installed && (install.canPrompt || install.ios) && (
        <Card className="mt-6">
          <div className="flex items-center gap-2 font-extrabold">
            <Download className="size-4" /> Put this app on your home screen
          </div>
          {install.canPrompt ? (
            <Button className="mt-3" size="sm" variant="secondary" onClick={() => install.prompt()}>
              Install
            </Button>
          ) : (
            <p className="mt-1 text-sm text-muted">
              Tap <b>Share</b>, then <b>Add to Home Screen</b>.
            </p>
          )}
        </Card>
      )}

      {passkeysSupported() && (
        <Button className="mt-6" variant="secondary" block onClick={() => setAddingPasskey(true)}>
          <Fingerprint className="size-4" /> Add a passkey on this device
        </Button>
      )}
      <Button
        className="mt-3"
        variant="secondary"
        block
        onClick={async () => {
          await logout();
          navigate("/kid-login");
        }}
      >
        <LogOut className="size-4" /> Switch user
      </Button>
      <AddKidPasskeySheet open={addingPasskey} onClose={() => setAddingPasskey(false)} />
      {editing && <ProfileSheet onClose={() => setEditing(false)} />}
    </div>
  );
}
